import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import type { TJobBoardChannel, TJobBoardChannelKey, TJobBoardPosting, TJobTeaserConnection, TJobTeaserPublicationType, TJobTeaserSettings } from '@bc-library/recruitment'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

const resource = 'jobBoards'

export type TJobBoardChannelState = TJobBoardChannel & {
  /** The board is open to the firm (API catalog config/integrations.json, or the server's JOB_BOARDS_READY) */
  isReady: boolean
  /** Site of the firm's list that stands for the board (null until the board is used) */
  website: null | {
    _id: string
    display: string
    /** The firm turned the board on: feed served, or API posting on */
    feedEnabled: boolean
    settings: Record<string, unknown> | null
  }
  /** Only for the boards posted through their API (postsByApi) */
  connection: TJobTeaserConnection | null
}

/** Public feed of the offers ticked for a board, read by the board itself */
export const getJobBoardFeedUrl = (firmId: string, channel: TJobBoardChannelKey, format: 'json' | 'xml' = 'xml') =>
  `${BASE_URL}/${API_MAIN_VERSION}/${resource}/feed/${firmId}/${channel}${format === 'xml' ? '?format=xml' : ''}`

const listChannels = (axios: IFactoryFunctionRequest<TJobBoardChannelState[], undefined>) => (): IFactoryResponse<TJobBoardChannelState[], undefined> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/channels`,
  })
}

type TUpdateChannelBody = {
  feedEnabled?: boolean
  applyToOpenOffers?: boolean
  settings?: TJobTeaserSettings
  /** null removes the firm's credentials, omitted keeps them; they are never sent back */
  secrets?: { clientId: string, clientSecret: string } | null
}
type TUpdateChannelResponse = TJobBoardChannelState & { addedOffers: number }

const updateChannel = (axios: IFactoryFunctionRequest<TUpdateChannelResponse, TUpdateChannelBody>) =>
  (channel: TJobBoardChannelKey, data: TUpdateChannelBody): IFactoryResponse<TUpdateChannelResponse, TUpdateChannelBody> => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/channels/${channel}`,
      data,
    })
  }

export type TTestChannelResponse = { ok: boolean, credentialsSource?: 'firm' | 'partner', code?: string, message?: string }

const testChannel = (axios: IFactoryFunctionRequest<TTestChannelResponse, undefined>) =>
  (channel: TJobBoardChannelKey): IFactoryResponse<TTestChannelResponse, undefined> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/channels/${channel}/test`,
    })
  }

/** Cursor page of a JobTeaser referential (pages of 30) */
export type TJobTeaserPage<T> = { items: T[], after: string | null, total: number | null }
export type TJobTeaserCategory = { id: string, name: string }
export type TJobTeaserSchool = { id: string, name: string, country: string, schoolType: string }
export type TJobTeaserSchoolBasket = { id: string, name: string, schools: { id: string, name: string }[] }

const listJobTeaserCategories = (axios: IFactoryFunctionRequest<TJobTeaserPage<TJobTeaserCategory>, undefined>) =>
  ({ after, lang }: { after?: string | null, lang: 'fr' | 'en' }): IFactoryResponse<TJobTeaserPage<TJobTeaserCategory>, undefined> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/jobteaser/categories`,
      query: after ? { after, lang } : { lang },
    })
  }

const listJobTeaserSchools = (axios: IFactoryFunctionRequest<TJobTeaserPage<TJobTeaserSchool>, undefined>) =>
  ({ after }: { after?: string | null } = {}): IFactoryResponse<TJobTeaserPage<TJobTeaserSchool>, undefined> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/jobteaser/schools`,
      query: after ? { after } : {},
    })
  }

const listJobTeaserSchoolBaskets = (axios: IFactoryFunctionRequest<TJobTeaserPage<TJobTeaserSchoolBasket>, undefined>) =>
  (): IFactoryResponse<TJobTeaserPage<TJobTeaserSchoolBasket>, undefined> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/jobteaser/school-baskets`,
    })
  }

const listOfferPostings = (axios: IFactoryFunctionRequest<TJobBoardPosting[], undefined>) =>
  (offerId: string): IFactoryResponse<TJobBoardPosting[], undefined> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/offers/${offerId}/postings`,
    })
  }

export type TPostingAction = 'sync' | 'publish' | 'upgrade' | 'refresh' | 'withdraw'
export type TPostingActionBody = { publicationType?: TJobTeaserPublicationType, scheduledOccurrences?: number, newType?: TJobTeaserPublicationType }
type TPostingActionResponse = { postings: TJobBoardPosting[] }

/**
 * sync: creates or updates the job ad now (archives it when the offer should not be posted);
 * publish: new publication or renewal ({ publicationType, scheduledOccurrences? }, uses the firm's credits);
 * upgrade: { newType }; refresh: reads the status on the board; withdraw: unticks the board and archives the job ad
 */
const runPostingAction = (axios: IFactoryFunctionRequest<TPostingActionResponse, TPostingActionBody>) =>
  (offerId: string, channel: TJobBoardChannelKey, action: TPostingAction, data?: TPostingActionBody): IFactoryResponse<TPostingActionResponse, TPostingActionBody> => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/offers/${offerId}/postings/${channel}/${action}`,
      data,
    })
  }

export default (axios: AxiosInstance) => ({
  listChannels: listChannels(useAxios(axios)),
  updateChannel: updateChannel(useAxios(axios)),
  testChannel: testChannel(useAxios(axios)),
  listJobTeaserCategories: listJobTeaserCategories(useAxios(axios)),
  listJobTeaserSchools: listJobTeaserSchools(useAxios(axios)),
  listJobTeaserSchoolBaskets: listJobTeaserSchoolBaskets(useAxios(axios)),
  listOfferPostings: listOfferPostings(useAxios(axios)),
  runPostingAction: runPostingAction(useAxios(axios)),
})
